import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useClerk } from "@clerk/clerk-react";
import { toast } from "sonner";
import { 
  LayoutDashboard, 
  Shield, 
  Users, 
  FolderKanban, 
  History, 
  CreditCard,
  Zap,
  LogOut,
  FileText,
  MessageSquareQuote,
  ShieldCheck,
  ClipboardList,
  Send,
  BarChart3,
  Mail,
  CalendarClock,
  UsersRound,
  Rocket,
  Award,
  Video,
  Share2,
  Palette,
  TrendingUp,
  Settings,
  UserCircle,
  Target,
  ChevronDown,
  ChevronRight,
  Briefcase,
  Bot,
  Megaphone,
  LineChart,
  UserCog,
  Star,
  Pin,
  Receipt,
  Sparkles,
  FileDiff
} from "lucide-react";

// All navigation items (flat list for easy lookup)
const allNavItems = {
  "/firewall": { path: "/firewall", icon: Shield, label: "Client Firewall", highlight: true },
  "/clients": { path: "/clients", icon: Users, label: "Clients" },
  "/projects": { path: "/projects", icon: FolderKanban, label: "Projects" },
  "/crm": { path: "/crm", icon: Target, label: "CRM Pipeline", isPremium: true },
  "/crm/reports": { path: "/crm/reports", icon: BarChart3, label: "CRM Reports", isPremium: true },
  "/invoices": { path: "/invoices", icon: Receipt, label: "Invoices", isPremium: true },
  "/scope-guard": { path: "/scope-guard", icon: ShieldCheck, label: "Scope Guard" },
  "/sop-generator": { path: "/sop-generator", icon: ClipboardList, label: "SOP Generator" },
  "/cold-outreach": { path: "/cold-outreach", icon: Send, label: "Cold Outreach" },
  "/outreach-pro": { path: "/outreach-pro", icon: Rocket, label: "Outreach Pro", isPremium: true },
  "/email-templates": { path: "/email-templates", icon: Mail, label: "Email Templates" },
  "/email-scheduler": { path: "/email-scheduler", icon: CalendarClock, label: "Email Scheduler" },
  "/proofs": { path: "/proofs", icon: Award, label: "Proof Builder", isPremium: true },
  "/video-testimonials": { path: "/video-testimonials", icon: Video, label: "Video Testimonials", isPremium: true },
  "/contract-templates": { path: "/contract-templates", icon: FileText, label: "Contracts", isPremium: true },
  "/templates": { path: "/templates", icon: Sparkles, label: "Template Library", isPremium: true },
  "/change-orders": { path: "/change-orders", icon: FileDiff, label: "Change Orders", isPremium: true },
  "/analytics": { path: "/analytics", icon: BarChart3, label: "Analytics" },
  "/advanced-analytics": { path: "/advanced-analytics", icon: TrendingUp, label: "Advanced Analytics", isPremium: true },
  "/affiliate": { path: "/affiliate", icon: Share2, label: "Affiliate Program", isPremium: true },
  "/branding": { path: "/branding", icon: Palette, label: "Custom Branding", isPremium: true },
  "/teams": { path: "/teams", icon: UsersRound, label: "Teams" },
  "/settings": { path: "/settings", icon: Settings, label: "Settings" },
  "/settings/security": { path: "/settings/security", icon: Shield, label: "Security", isPremium: false },
  "/history": { path: "/history", icon: History, label: "Message History" },
  "/billing": { path: "/billing", icon: CreditCard, label: "Billing & Plans" },
};

// Grouped navigation structure
const navGroups = [
  {
    id: "clients",
    label: "Clients & Sales",
    icon: Briefcase,
    items: [
      allNavItems["/firewall"],
      allNavItems["/clients"],
      allNavItems["/projects"],
      allNavItems["/crm"],
      allNavItems["/crm/reports"],
      allNavItems["/invoices"],
    ]
  },
  {
    id: "ai-tools",
    label: "AI Tools",
    icon: Bot,
    items: [
      allNavItems["/scope-guard"],
      allNavItems["/sop-generator"],
    ]
  },
  {
    id: "outreach",
    label: "Outreach",
    icon: Megaphone,
    items: [
      allNavItems["/cold-outreach"],
      allNavItems["/outreach-pro"],
      allNavItems["/email-templates"],
      allNavItems["/email-scheduler"],
    ]
  },
  {
    id: "marketing",
    label: "Marketing",
    icon: Award,
    items: [
      allNavItems["/proofs"],
      allNavItems["/video-testimonials"],
    ]
  },
  {
    id: "documents",
    label: "Documents",
    icon: FileText,
    items: [
      allNavItems["/contract-templates"],
      allNavItems["/templates"],
      allNavItems["/change-orders"],
    ]
  },
  {
    id: "analytics",
    label: "Analytics",
    icon: LineChart,
    items: [
      allNavItems["/analytics"],
      allNavItems["/advanced-analytics"],
    ]
  },
  {
    id: "account",
    label: "Account",
    icon: UserCog,
    items: [
      allNavItems["/affiliate"],
      allNavItems["/branding"],
      allNavItems["/teams"],
      allNavItems["/settings"],
      allNavItems["/settings/security"],
      allNavItems["/history"],
      allNavItems["/billing"],
    ]
  },
];

// Local storage key for favorites
const FAVORITES_KEY = "clientkit_favorites";

export default function Sidebar({ isOpen, currentPath, onClose }) {
  const navigate = useNavigate();
  const { signOut: clerkSignOut } = useClerk();
  
  // Load favorites from localStorage
  const [favorites, setFavorites] = useState(() => {
    try {
      const saved = localStorage.getItem(FAVORITES_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Save favorites to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
  }, [favorites]);
  
  // Track which groups are expanded
  const getInitialExpandedGroups = () => {
    const expanded = {};
    navGroups.forEach(group => {
      const hasActivePath = group.items.some(item => item.path === currentPath);
      expanded[group.id] = hasActivePath;
    });
    return expanded;
  };
  
  const [expandedGroups, setExpandedGroups] = useState(getInitialExpandedGroups);

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const toggleFavorite = (path, e) => {
    e.stopPropagation();
    setFavorites(prev => {
      if (prev.includes(path)) {
        return prev.filter(p => p !== path);
      } else {
        return [...prev, path];
      }
    });
  };

  const handleLogout = async () => {
    try {
      if (window.Clerk?.session) {
        await clerkSignOut();
      }
      toast.success("Logged out successfully");
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
      navigate("/", { replace: true });
    }
  };

  const handleNavClick = (path) => {
    navigate(path);
    if (window.innerWidth < 1024 && onClose) {
      onClose();
    }
  };

  // Get favorite items
  const favoriteItems = favorites
    .map(path => allNavItems[path])
    .filter(Boolean);

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={onClose}
        />
      )}
      
      <aside 
        className={`
          fixed lg:relative z-40
          ${isOpen ? "w-64 translate-x-0" : "w-0 -translate-x-full lg:translate-x-0"}
          bg-slate-900/95 lg:bg-slate-900/50 
          border-r border-slate-700/50 
          transition-all duration-300 
          overflow-hidden flex flex-col
          h-full
        `}
      >
        <div className="p-4 lg:p-6 flex-1 overflow-y-auto">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-6 lg:mb-8">
            <img src="/logo.png" alt="ClientKit" className="w-11 h-11 lg:w-12 lg:h-12 object-contain flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="text-lg lg:text-xl font-bold text-white truncate">ClientKit</h1>
              <p className="text-xs text-slate-400">Freelancer OS</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="space-y-1">
            {/* Dashboard - Always visible, standalone */}
            <button
              onClick={() => handleNavClick("/dashboard")}
              data-testid="nav-dashboard"
              className={`flex items-center gap-3 px-3 lg:px-4 py-2.5 lg:py-3 rounded-xl transition-all w-full text-left ${
                currentPath === "/dashboard"
                  ? "bg-indigo-500/20 text-indigo-400 border border-indigo-500/30"
                  : "text-slate-300 hover:bg-slate-800 hover:text-slate-200 border border-transparent"
              }`}
            >
              <LayoutDashboard className="w-5 h-5 flex-shrink-0" />
              <span className="font-medium">Dashboard</span>
            </button>

            {/* Favorites Section */}
            {favoriteItems.length > 0 && (
              <div className="pt-3 pb-2">
                <div className="flex items-center gap-2 px-3 mb-2">
                  <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Favorites</span>
                </div>
                <div className="space-y-0.5">
                  {favoriteItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = currentPath === item.path;
                    
                    return (
                      <div key={`fav-${item.path}`} className="group relative">
                        <div
                          onClick={() => handleNavClick(item.path)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === 'Enter' && handleNavClick(item.path)}
                          data-testid={`nav-fav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`flex items-center gap-3 px-3 lg:px-4 py-2 rounded-xl transition-all w-full text-left cursor-pointer ${
                            isActive
                              ? "bg-indigo-500/20 text-indigo-400 border border-indigo-500/30"
                              : "text-slate-300 hover:bg-slate-800 hover:text-slate-200 border border-transparent"
                          }`}
                        >
                          <Icon className="w-4 h-4 flex-shrink-0" />
                          <span className="font-medium text-sm truncate">{item.label}</span>
                          <button
                            onClick={(e) => toggleFavorite(item.path, e)}
                            className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-slate-700 rounded"
                            title="Remove from favorites"
                          >
                            <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="border-b border-slate-700/50 mt-3 mb-2" />
              </div>
            )}

            {/* Grouped Navigation */}
            <div className="space-y-1">
              {navGroups.map((group) => {
                const GroupIcon = group.icon;
                const isExpanded = expandedGroups[group.id];
                const hasActiveItem = group.items.some(item => item.path === currentPath);
                
                return (
                  <div key={group.id} className="space-y-0.5">
                    {/* Group Header */}
                    <button
                      onClick={() => toggleGroup(group.id)}
                      className={`flex items-center gap-3 px-3 lg:px-4 py-2 rounded-xl transition-all w-full text-left ${
                        hasActiveItem
                          ? "text-indigo-400"
                          : "text-slate-400 hover:text-slate-200"
                      }`}
                    >
                      <GroupIcon className="w-4 h-4 flex-shrink-0" />
                      <span className="font-medium text-sm flex-1">{group.label}</span>
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-slate-500" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-slate-500" />
                      )}
                    </button>

                    {/* Group Items */}
                    {isExpanded && (
                      <div className="ml-3 pl-4 border-l border-slate-700/50 space-y-0.5">
                        {group.items.map((item) => {
                          const Icon = item.icon;
                          const isActive = currentPath === item.path;
                          const isFavorite = favorites.includes(item.path);
                          
                          return (
                            <div key={item.path} className="group relative">
                              <div
                                onClick={() => handleNavClick(item.path)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => e.key === 'Enter' && handleNavClick(item.path)}
                                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all w-full text-left text-sm cursor-pointer ${
                                  isActive
                                    ? "bg-indigo-500/20 text-indigo-400"
                                    : item.highlight
                                    ? "text-slate-300 hover:bg-indigo-500/10 hover:text-indigo-400"
                                    : "text-slate-400 hover:bg-slate-800 hover:text-slate-200"
                                }`}
                              >
                                <Icon className={`w-4 h-4 flex-shrink-0 ${item.highlight && !isActive ? "text-indigo-400" : ""}`} />
                                <span className="truncate flex-1">{item.label}</span>
                                
                                {/* Favorite star - show filled if favorite, outline on hover */}
                                <button
                                  onClick={(e) => toggleFavorite(item.path, e)}
                                  className={`p-1 rounded transition-all ${
                                    isFavorite 
                                      ? "opacity-100" 
                                      : "opacity-0 group-hover:opacity-100"
                                  } hover:bg-slate-700`}
                                  title={isFavorite ? "Remove from favorites" : "Add to favorites"}
                                >
                                  <Star className={`w-3 h-3 ${
                                    isFavorite 
                                      ? "text-amber-400 fill-amber-400" 
                                      : "text-slate-500 hover:text-amber-400"
                                  }`} />
                                </button>

                                {/* Badges - only show if not showing star */}
                                {!isFavorite && item.highlight && !isActive && (
                                  <span className="text-[10px] bg-indigo-500/20 text-indigo-400 px-1.5 py-0.5 rounded-full flex-shrink-0 group-hover:hidden">
                                    Core
                                  </span>
                                )}
                                {!isFavorite && item.isPremium && !isActive && (
                                  <span className="text-[10px] bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded-full flex-shrink-0 group-hover:hidden">
                                    Pro
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </nav>
        </div>

        {/* Bottom Section */}
        <div className="p-3 lg:p-4 border-t border-slate-700/50">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 lg:px-4 py-2.5 lg:py-3 w-full rounded-xl text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition-all"
            data-testid="sidebar-logout-btn"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            <span className="font-medium">Sign Out</span>
          </button>
          <div className="text-xs text-slate-500 text-center mt-3 lg:mt-4">
            <p>ClientKit v1.0</p>
          </div>
        </div>
      </aside>
    </>
  );
}
